import { alphaMutationRejected } from "../alpha-mutation";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { api } from "../api";
import { ErrorState, Loading } from "../ui/State";
export function RefundRequestsAlpha({
  premises,
  accounts,
}: {
  premises: string;
  accounts: { id: string; reference: string; available: boolean }[];
}) {
  const root = "/api/portal/leaseholder/" + premises + "/refunds",
    [model, setModel] = useState<any>(),
    [error, setError] = useState<unknown>(),
    [busy, setBusy] = useState(false),
    pending = useRef<any>(null);
  const load = () => api(root).then(setModel);
  useEffect(() => {
    void load().catch(setError);
  }, [root]);
  async function send(body: any) {
    pending.current ??= { ...body, idempotencyKey: crypto.randomUUID() };
    setBusy(true);
    try {
      await api(root, {
        method: "POST",
        body: JSON.stringify(pending.current),
      });
      pending.current = null;
      setError(undefined);
      await load();
    } catch (e) {
      if (alphaMutationRejected(e)) pending.current = null;
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="panel">
      <h2>Credit refunds</h2>
      {Boolean(error) && <ErrorState error={error} />}{" "}
      {pending.current && !busy && (
        <button onClick={() => void send({})}>Retry the same request</button>
      )}
      {model?.requests.map((r: any) => (
        <p key={r.id}>
          £{r.amount} · {r.status}
          {r.settlement_needs_review
            ? " — Settlement needs review after a reversal"
            : ""}{" "}
          · {r.review_note ?? r.reason}
        </p>
      ))}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const f = new FormData(e.currentTarget);
          void send({
            accountId: f.get("account"),
            amount: f.get("amount"),
            reason: f.get("reason"),
          });
        }}
      >
        <fieldset disabled={busy || !!pending.current}>
          <legend>Request a refund of account credit</legend>
          <label>
            Account
            <select name="account" required>
              <option value="">Choose account</option>
              {accounts
                .filter((a) => a.available)
                .map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.reference}
                  </option>
                ))}
            </select>
          </label>
          <label>
            Amount
            <input
              name="amount"
              inputMode="decimal"
              required
              pattern="[0-9]+(\.[0-9]{1,2})?"
            />
          </label>
          <label>
            Reason
            <textarea name="reason" required minLength={5} maxLength={2000} />
          </label>
          <p>
            Your manager will review entitlement and arrange payment separately.
            Do not include bank details here.
          </p>
          <button>Request refund</button>
        </fieldset>
      </form>
    </section>
  );
}
export function RefundReviewAlpha() {
  const [q] = useSearchParams(),
    building = q.get("building"),
    [model, setModel] = useState<any>(),
    [error, setError] = useState<unknown>(),
    [busy, setBusy] = useState(false),
    pending = useRef<{ id: string; body: any } | null>(null);
  const load = () =>
    building
      ? api("/api/alpha/refunds?building=" + building).then(setModel)
      : Promise.resolve();
  useEffect(() => {
    setModel(undefined);
    void load().catch(setError);
  }, [building]);
  async function send(id: string, body: any) {
    pending.current ??= {
      id,
      body: { ...body, idempotencyKey: crypto.randomUUID() },
    };
    setBusy(true);
    try {
      await api("/api/alpha/refunds/" + pending.current.id + "/review", {
        method: "POST",
        body: JSON.stringify(pending.current.body),
      });
      pending.current = null;
      setError(undefined);
      await load();
    } catch (e) {
      if (alphaMutationRejected(e)) pending.current = null;
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <h1>Refund requests</h1>
      {model?.requests.length===0&&<p>No refund requests are recorded for this building.</p>}
      {Boolean(error) && <ErrorState error={error} />}{" "}
      {pending.current && !busy && (
        <button onClick={() => void send("", {})}>Retry the same review</button>
      )}
      {!building ? (
        <p>Choose a building.</p>
      ) : !model ? (
        <Loading />
      ) : (
        model.requests.map((r: any) => (
          <section className="panel" key={r.id}>
            <h2>
              {r.account_reference} · £{r.amount}
            </h2>
            <p>
              {r.reason} · {r.status}
            </p>
            <p>{r.review_note}</p>
            {r.settlement_needs_review && (
              <p role="alert">
                Settlement evidence was reversed. Review the refund record and
                bank allocation in Finance before further action.
              </p>
            )}
            {!["Recorded", "Rejected"].includes(r.status) && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const f = new FormData(e.currentTarget);
                  void send(r.id, {
                    decision: f.get("decision"),
                    note: f.get("note"),
                    ...(f.get("adjustment")
                      ? { adjustmentId: f.get("adjustment") }
                      : {}),
                    ...(f.get("bank")
                      ? { bankTransactionId: f.get("bank") }
                      : {}),
                  });
                }}
              >
                <fieldset disabled={busy || !!pending.current}>
                  <legend>Review request</legend>
                  <label>
                    Decision
                    <select name="decision">
                      {r.status === "Requested" ? (
                        <option>Accept</option>
                      ) : (
                        <option>Record settlement</option>
                      )}
                      <option>Reject</option>
                    </select>
                  </label>
                  <label>
                    Note
                    <textarea
                      name="note"
                      required
                      minLength={5}
                      maxLength={2000}
                    />
                  </label>
                  {r.status === "Accepted" && (
                    <>
                      <p>
                        Use the existing income-control refund process. Once
                        approved and paid, link its exact accounting and bank
                        evidence.
                      </p>
                      <label>
                        Approved refund record
                        <select name="adjustment">
                          <option value="">Choose matching refund</option>
                          {model.refundRecords
                            .filter(
                              (a: any) =>
                                a.account_id === r.account_id &&
                                a.amount === r.amount,
                            )
                            .map((a: any) => (
                              <option key={a.id} value={a.id}>
                                {a.source_reference} · £{a.amount}
                              </option>
                            ))}
                        </select>
                      </label>
                      <label>
                        Matching unallocated bank debit
                        <select name="bank">
                          <option value="">Choose original debit</option>
                          {model.bankDebits
                            .filter(
                              (b: any) =>
                                b.bank_account_id === r.bank_account_id &&
                                b.amount === "-" + r.amount,
                            )
                            .map((b: any) => (
                              <option key={b.id} value={b.id}>
                                {b.transaction_date} · {b.description} · £
                                {b.amount}
                              </option>
                            ))}
                        </select>
                      </label>
                    </>
                  )}
                  <p>Accepting a request does not authorise or send money.</p>
                  <button>Save review</button>
                </fieldset>
              </form>
            )}
          </section>
        ))
      )}
    </>
  );
}
